import { useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router";
import { useAuth } from "../auth/AuthProvider";
import { ErrorMessage, Loading } from "../components/StatusMessage";
import { useI18n } from "../i18n/I18nProvider";
import { useAsync } from "../lib/useAsync";
import { marketApi, type Want } from "./api";
import { ApiError, imageUrl } from "../lib/api";
import type { MessageKey } from "../i18n/messages";
import { CardPicker } from "./CardPicker";
import { Avatar, ListingGrid, useArs } from "./components";
import { OffersList } from "./OffersList";
import { OrderRows } from "./SellerDashboard";
import { CountBadge, useInbox } from "./InboxProvider";
import { useCouponOff } from "./CouponsTab";

const TABS = ["compras", "ofertas", "favoritos", "busco", "seguidos", "cupones"] as const;
type Tab = (typeof TABS)[number];

/** /cuenta/compras — the buyer's side: purchases, offers, saved listings, "Busco" and followed sellers. */
export function BuyerHub() {
  const { t } = useI18n();
  const { inbox } = useInbox();
  const auth = useAuth();
  const [params, setParams] = useSearchParams();
  if (!auth.loading && !auth.user) return <Navigate to="/ingresar?next=%2Fcuenta%2Fcompras" replace />;
  const tab: Tab = (TABS as readonly string[]).includes(params.get("tab") ?? "") ? (params.get("tab") as Tab) : "compras";
  return (
    <div className="container section buyer-hub">
      <p className="breadcrumbs">
        <Link to="/cuenta">{t("account.title")}</Link> › {t("mkt.myMarket")}
      </p>
      <h1>{t("mkt.myMarket")}</h1>
      <div className="tabs" role="tablist">
        {TABS.map((k) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? "tab is-active" : "tab"} onClick={() => setParams({ tab: k })}>
            {t(`mkt.tab.${k}` as never)} <CountBadge count={k === "compras" ? inbox.purchases : k === "ofertas" ? inbox.buyerOffers : 0} />
          </button>
        ))}
      </div>
      <div role="tabpanel">
        {tab === "compras" && <Purchases />}
        {tab === "ofertas" && <OffersList role="buyer" />}
        {tab === "favoritos" && <Favorites />}
        {tab === "busco" && <Wants />}
        {tab === "seguidos" && <Following />}
        {tab === "cupones" && <MyCoupons />}
      </div>
    </div>
  );
}

function Purchases() {
  const { t, locale } = useI18n();
  const ars = useArs();
  const orders = useAsync((signal) => marketApi.orders("buyer", signal), []);
  if (orders.error) return <ErrorMessage onRetry={orders.reload} />;
  if (!orders.data) return <Loading />;
  if (!orders.data.length)
    return (
      <div className="status">
        <p>{t("mkt.noPurchases")}</p>
        <Link to="/mercado" className="btn btn-primary">
          {t("mkt.goToMarket")}
        </Link>
      </div>
    );
  return <OrderRows rows={orders.data} ars={ars} locale={locale} attention={(o) => o.status === "pending_payment" || o.status === "shipped"} />;
}

function Favorites() {
  const { t } = useI18n();
  const favs = useAsync((signal) => marketApi.favorites(signal), []);
  if (favs.error) return <ErrorMessage onRetry={favs.reload} />;
  if (!favs.data) return <Loading />;
  return favs.data.length ? <ListingGrid items={favs.data} /> : <p className="muted">{t("mkt.noFavorites")}</p>;
}

function Wants() {
  const { t, locale } = useI18n();
  const ars = useArs();
  const wants = useAsync((signal) => marketApi.wants(signal), []);
  const [list, setList] = useState<Want[] | null>(null);
  const [max, setMax] = useState("");
  const [failed, setFailed] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  if (wants.error) return <ErrorMessage onRetry={wants.reload} />;
  if (!wants.data) return <Loading />;
  const rows = list ?? wants.data;
  const date = (iso: string) => new Date(iso).toLocaleDateString(locale === "es" ? "es-AR" : "en-US", { day: "numeric", month: "short" });
  /** A change from a row: the answer is the whole list again. */
  const change = async (productId: number, c: Parameters<typeof marketApi.updateWant>[1]) => {
    setBusy(productId);
    setFailed(null);
    try {
      setList(await marketApi.updateWant(productId, c));
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "";
      setFailed(["too_many_public", "bump_too_soon", "fulfilled", "blocked"].includes(code) ? t(`wanted.err.${code}` as MessageKey) : t("error.generic"));
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="stack">
      <p className="muted">{t("mkt.wantsIntro")}</p>
      <p className="muted small">{t("mkt.wantsPrivacy")}</p>
      <p>
        <Link to="/se-busca/publicar" className="btn btn-outline btn-sm">
          {t("wanted.post")}
        </Link>{" "}
        <Link to="/se-busca" className="btn btn-link btn-sm">
          {t("nav.wantedBoard")}
        </Link>
      </p>
      <div className="field-row">
        <div className="field">
          <label htmlFor="want-max">{t("mkt.wantMax")}</label>
          <input id="want-max" type="number" min={1} inputMode="numeric" value={max} onChange={(e) => setMax(e.target.value)} placeholder={t("mkt.f.any")} />
        </div>
      </div>
      <CardPicker
        filters
        label={t("mkt.wantAdd")}
        onPick={async (card) => {
          setFailed(null);
          try {
            // Empty: a card already on the list keeps its price.
            setList(await marketApi.addWant(card.id, max ? Number(max) : undefined));
            setMax("");
          } catch {
            setFailed(t("error.generic"));
          }
        }}
      />
      {failed && (
        <p className="form-error" role="alert">
          {failed}
        </p>
      )}
      {rows.length === 0 ? (
        <p className="muted">{t("mkt.noWants")}</p>
      ) : (
        <ul className="want-list">
          {rows.map((w) => {
            const onBoard = w.onBoardUntil !== null;
            const canBump = onBoard && w.bumpFrom !== null && new Date(w.bumpFrom).getTime() <= Date.now();
            return (
              <li key={w.productId} className="want-row">
                <img src={imageUrl(w.imageKey)} alt="" width={40} height={56} />
                <div>
                  <strong>{w.name}</strong>
                  <span className="muted small">
                    {[w.setName, w.cardNumber ? `#${w.cardNumber}` : null].filter(Boolean).join(" · ")}
                    {w.maxPriceArs ? ` · ${t("mkt.upTo", { price: ars(w.maxPriceArs) })}` : ""}
                  </span>
                  <span className="want-status small">
                    {w.fulfilledAt ? t("wanted.fulfilled") : w.hidden && onBoard ? `⚠️ ${t("wanted.hidden")}` : onBoard ? t("wanted.onBoardUntil", { date: date(w.onBoardUntil!) }) : t("wanted.private")}
                  </span>
                </div>
                <div className="want-actions">
                  {w.listings > 0 ? (
                    <Link to={`/producto/${w.slug}#ofertas`} className="btn btn-outline btn-sm">
                      {t("mkt.wantFound", { count: w.listings, price: ars(w.minPriceArs ?? 0) })}
                    </Link>
                  ) : (
                    <span className="muted small">{t("mkt.wantWaiting")}</span>
                  )}
                  {!w.fulfilledAt && (
                    <label className="check small">
                      <input type="checkbox" checked={onBoard} disabled={busy === w.productId} onChange={(e) => void change(w.productId, { public: e.target.checked })} /> {t("wanted.visible")}
                    </label>
                  )}
                  {onBoard && !w.hidden && (
                    <>
                      <button type="button" className="btn btn-link btn-sm" disabled={!canBump || busy === w.productId} title={canBump ? undefined : t("wanted.bumpFrom", { date: date(w.bumpFrom!) })} onClick={() => void change(w.productId, { bump: true })}>
                        ⬆ {t("wanted.bump")}
                      </button>
                      <Link to={`/se-busca/${encodeURIComponent(w.slug)}`} state={{ fresh: true }} className="btn btn-link btn-sm">
                        {t("wanted.view")}
                      </Link>
                    </>
                  )}
                  {!w.fulfilledAt && (
                    <Link to={`/se-busca/publicar?carta=${encodeURIComponent(w.slug)}`} className="btn btn-link btn-sm">
                      {t("wanted.edit")}
                    </Link>
                  )}
                  <button type="button" className="btn btn-link btn-sm" disabled={busy === w.productId} onClick={() => void change(w.productId, { fulfilled: !w.fulfilledAt })}>
                    {w.fulfilledAt ? t("wanted.wantAgain") : t("wanted.gotIt")}
                  </button>
                  <button
                    type="button"
                    className="btn btn-link btn-sm"
                    onClick={() =>
                      void marketApi
                        .deleteWant(w.productId)
                        .then(() => setList(rows.filter((x) => x.productId !== w.productId)))
                        .catch(() => setFailed(t("error.generic")))
                    }
                  >
                    {t("mkt.remove")}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function Following() {
  const { t } = useI18n();
  const list = useAsync((signal) => marketApi.following(signal), []);
  if (list.error) return <ErrorMessage onRetry={list.reload} />;
  if (!list.data) return <Loading />;
  if (!list.data.length) return <p className="muted">{t("mkt.noFollowing")}</p>;
  return (
    <ul className="following-list">
      {list.data.map((s) => (
        <li key={s.handle}>
          <Link to={`/vendedores/${s.handle}`} className="seller-chip">
            <Avatar url={s.avatarUrl} name={s.displayName} size={32} />
            <span>
              <strong>{s.displayName}</strong> <span className="muted small">@{s.handle} · {t("mkt.available", { count: s.listings })}</span>
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

/** Coupons shops gave this buyer (personal, or for a group they're in), to use at each shop's checkout. */
function MyCoupons() {
  const { t, locale } = useI18n();
  const off = useCouponOff();
  const list = useAsync((signal) => marketApi.myCoupons(null, signal), []);
  if (list.error) return <ErrorMessage onRetry={list.reload} />;
  if (!list.data) return <Loading />;
  if (!list.data.length) return <p className="muted">{t("myc.empty")}</p>;
  return (
    <div className="stack">
      <p className="muted small">{t("myc.intro")}</p>
      <ul className="coupon-list">
        {list.data.map((c) => (
          <li key={`${c.seller.handle}-${c.code}`} className="coupon-row">
            <div>
              <span className="coupon-chip">{c.code}</span> {off(c)}
              {c.personal && <span className="muted small"> · {t("myc.personal")}</span>}
              <div className="small">
                <Link to={`/vendedores/${c.seller.handle}`}>{c.seller.displayName}</Link>
                {c.endsAt && <span className="muted"> · {t("myc.until", { date: new Date(c.endsAt).toLocaleDateString(locale === "es" ? "es-AR" : "en-US") })}</span>}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
